import { EventEmitter } from 'eventemitter3';

import type { LogLevel, Logger, LoggerEventEmitter } from '../logger.types';

/**
 * Configuration options for the logger
 */
type CreateLoggerOptions = {
  level?: LogLevel;
};

/**
 * Log levels and their corresponding numeric values
 */
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  error: 3,
  info: 1,
  warn: 2,
};

// Assign to avoid lint errors
const defaultConsole = console;

/**
 * Creates a logger instance with configurable log level and event emission capabilities.
 * @returns A logger instance with debug, info, warn, and error methods
 */
export const createLogger = (options: CreateLoggerOptions = {}): Logger => {
  const eventEmitter: LoggerEventEmitter = new EventEmitter();

  // Default to 'debug' if no level is provided
  const minLevel: LogLevel = options.level ?? 'warn';

  // Helper to decide if we should log based on current log level
  const shouldLog = (level: LogLevel): boolean => {
    return LOG_LEVELS[level] >= LOG_LEVELS[minLevel];
  };

  // Helper to format the log message
  const formatMessage = (level: LogLevel, message: string): string => {
    const timestamp = new Date().toISOString();

    return `[${timestamp}] ${level.toUpperCase()}: ${message}`;
  };

  // Unified logger function that checks the level and calls the appropriate console method
  const log = (
    level: LogLevel,
    consoleMethod: (...data: unknown[]) => void,
    message: string,
    ...args: unknown[]
  ) => {
    eventEmitter.emit('log', level, message, ...args);

    if (!shouldLog(level)) return;

    consoleMethod(formatMessage(level, message), ...args);
  };

  return {
    debug: (message, ...args) =>
      log('debug', defaultConsole.debug, message, ...args),

    error: (message, ...args) =>
      log('error', defaultConsole.error, message, ...args),

    info: (message, ...args) =>
      log('info', defaultConsole.info, message, ...args),

    off: eventEmitter.off.bind(eventEmitter),

    on: eventEmitter.on.bind(eventEmitter),
    warn: (message, ...args) =>
      log('warn', defaultConsole.warn, message, ...args),
  };
};
